import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, HeartHandshake, GraduationCap, FileCheck, Plane, CarFront, Handshake, BadgePercent, ShieldCheck, BookOpen } from "lucide-react";
import { MaskedLine, FadeUp, Stagger, StaggerItem, Magnetic } from "@/components/motion-primitives";
import { useConsult } from "@/components/ConsultProvider";

const JOURNEY_QUESTIONS = [
  "Am I choosing the right university that is suitable for me?",
  "Did I give all the required tests or not?",
  "Am I aware of all the information about the deadline of the universities?",
  "Are all the required documents arranged & attested properly as required?",
  "Did I fill the required forms for application with required and complete information?",
];

const ADMISSION_SERVICES = [
  { icon: HeartHandshake, title: "Free Counseling", desc: "All of our counseling sessions are free. Our friendly team makes your process of applying abroad as smooth as possible." },
  { icon: GraduationCap, title: "Admission Assistance", desc: "Complete assistance for admission into the university of your choice — and for your future success beyond it." },
  { icon: FileCheck, title: "Visa Guidance Assistance", desc: "Accurate, genuine advice on visas for students who want to study abroad and build a better future." },
  { icon: Plane, title: "Travel Assistance", desc: "Travel tips and airline booking assistance — every effort made to keep your journey seamless." },
  { icon: CarFront, title: "Airport Pickups", desc: "Airport pick-up and accommodation arranged when you land, for a smooth, safe, stress-free arrival." },
  { icon: Handshake, title: "Effortless Coordination", desc: "We guide all future communication with university officials, ensuring clarity and confidence throughout." },
];

const HIGHLIGHTS = [
  { icon: BadgePercent, label: "Scholarship & funding guidance available" },
  { icon: ShieldCheck, label: "Visa process made simple and stress-free" },
  { icon: BookOpen, label: "Test prep for IELTS, GRE, TOEFL" },
];

export default function Admissions() {
  const { openConsult } = useConsult();
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} data-testid="admissions-page">
      <section className="relative overflow-hidden bg-navy pb-24 pt-40 text-white" data-testid="admissions-hero">
        <div className="noise-overlay absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6">
          <MaskedLine delay={0.9}>
            <span className="font-accent text-xs font-semibold uppercase tracking-[0.3em] text-gold">Admissions</span>
          </MaskedLine>
          <h1 className="mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl">
            <MaskedLine delay={1.0}>Your admission,</MaskedLine>
            <MaskedLine delay={1.12}><span className="italic text-gold">done right.</span></MaskedLine>
          </h1>
          <MaskedLine delay={1.3}>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">
              The application procedure is one of the most significant steps in studying abroad. A properly filed, on-time application protects your money, your time — and your future admission prospects.
            </p>
          </MaskedLine>
        </div>
      </section>

      <section className="border-b border-line bg-white py-10" data-testid="admissions-highlights">
        <div className="mx-auto flex max-w-7xl flex-wrap justify-center gap-x-12 gap-y-4 px-6">
          {HIGHLIGHTS.map((h) => (
            <span key={h.label} className="flex items-center gap-2.5 text-base font-medium text-navy">
              <h.icon className="h-5 w-5 text-gold" /> {h.label}
            </span>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 md:py-32" data-testid="admissions-journey">
        <FadeUp>
          <p className="font-accent text-xs font-bold uppercase tracking-[0.3em] text-gold">Your Step-by-Step Admission Journey</p>
          <h2 className="mt-4 max-w-2xl font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-5xl">
            Five questions every application <span className="italic text-gold">must answer</span>
          </h2>
        </FadeUp>
        <Stagger className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" gap={0.07}>
          {JOURNEY_QUESTIONS.map((q, i) => (
            <StaggerItem key={q}>
              <div data-testid={`journey-card-${i}`} className="group flex h-full gap-5 rounded-xl border border-line bg-white p-7 shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:border-gold/50 hover:shadow-lift">
                <span className="font-heading text-4xl font-extrabold text-navy/10 transition-colors duration-300 group-hover:text-gold/50">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <p className="text-base font-medium leading-relaxed text-navy">{q}</p>
                  <p className="mt-3 flex items-center gap-1.5 text-sm text-muted">
                    <CheckCircle2 className="h-4 w-4 text-gold" /> We handle this with you
                  </p>
                </div>
              </div>
            </StaggerItem>
          ))}
          <StaggerItem>
            <button
              onClick={() => openConsult()}
              data-testid="journey-cta-card"
              className="group flex h-full w-full flex-col justify-between rounded-xl bg-navy p-7 text-left shadow-lift transition-transform duration-300 hover:-translate-y-1.5"
            >
              <p className="font-heading text-xl font-bold leading-snug text-white">
                Unsure about any of these? <span className="text-gold">That's exactly what we're here for.</span>
              </p>
              <span className="mt-6 inline-flex items-center gap-2 font-accent text-xs font-bold uppercase tracking-widest text-gold">
                Get free counseling <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </button>
          </StaggerItem>
        </Stagger>
      </section>

      <section className="border-t border-line bg-white py-24 md:py-32" data-testid="admissions-services">
        <div className="mx-auto max-w-7xl px-6">
          <FadeUp>
            <p className="font-accent text-xs font-bold uppercase tracking-[0.3em] text-gold">How We Help</p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-5xl">
              From first counseling to <span className="italic text-gold">first day on campus</span>
            </h2>
          </FadeUp>
          <Stagger className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" gap={0.07}>
            {ADMISSION_SERVICES.map((s, i) => (
              <StaggerItem key={s.title}>
                <div data-testid={`admission-service-${i}`} className="group h-full rounded-xl border border-line bg-surface p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-gold/50 hover:bg-white hover:shadow-lift">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy/5 text-navy transition-colors duration-300 group-hover:bg-gold/15 group-hover:text-gold">
                    <s.icon className="h-6 w-6" strokeWidth={1.75} />
                  </span>
                  <h3 className="mt-6 font-heading text-lg font-bold uppercase tracking-wide text-navy">{s.title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-muted">{s.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <FadeUp delay={0.15} className="mt-16 text-center">
            <Magnetic>
              <button
                onClick={() => openConsult()}
                data-testid="admissions-consult-button"
                className="group inline-flex items-center gap-2 rounded-full bg-navy px-9 py-4 font-accent text-sm font-bold uppercase tracking-widest text-white transition-colors duration-300 hover:bg-gold hover:text-navy"
              >
                Start Your Application
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </Magnetic>
          </FadeUp>
        </div>
      </section>
    </motion.div>
  );
}
